import React from 'react';
import BusyButton, { BusyButtonProps } from './BusyButton';
import { observer } from 'mobx-react';
import { makeObservable, observable } from 'mobx';
import { LoadingTracker, LoadStatus } from '~/lib/store/LoadingTracker';
import { getAppContext } from '~/lib/AppContext';
import { isAbortError } from '~/lib/helper/ErrorHelper';

export type AsyncButtonProps = Omit<BusyButtonProps, 'onClick'> & {
    onClick?: (e: React.MouseEvent) => unknown;
    busy?: boolean;
    id?: string;
    /** Human context for what failed (e.g. "Unable to reboot server"), shown as a
     *  lead-in above the structured error detail when an `onClick` rejection is routed to the error
     *  modal — the `AsyncButton`/`ActionButton` equivalent of `FormStore.doSubmit`'s `errorSummary`.
     *
     *  Inert when `onClick` wraps `FormStore.doSubmit()`: that never re-throws, so the catch below
     *  never fires and this copy never reaches the modal — `doSubmit()` shows its own modal instead.
     *  Pass `errorSummary` to `doSubmit` in that case (see `Rescue.tsx` / `Reinstall.tsx`). */
    errorSummary?: string;
};

@observer
export class AsyncButton extends React.Component<AsyncButtonProps> {
    @observable
    loader = new LoadingTracker();

    constructor(props: AsyncButtonProps) {
        super(props);
        makeObservable(this);
    }

    render() {
        const { busy, errorSummary, ...rest } = this.props;
        const loaderBusy = this.loader.status === LoadStatus.Loading;
        const onClick = this.props.onClick
            ? async (e: React.MouseEvent) => {
                  try {
                      await this.loader.track(() => this.props.onClick?.(e));
                  } catch (err) {
                      if (!isAbortError(err)) {
                          getAppContext().error(err, errorSummary ? { errorSummary } : undefined);
                      }
                  }
              }
            : undefined;
        return <BusyButton busy={loaderBusy || (busy ?? false)} {...rest} onClick={onClick} />;
    }
}
